import toNumber from 'lodash/toNumber';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import { getIdFromGid } from '../../utils';

enum LimitTypesEnum {
  MINIMUM = 'awt-min-size',
  MAXIMUM = 'awt-max-size',
  BOTH = 'awt-both-size',
}

export const sortProductsBySelected = (products = [], selected = {}) => {
  const sortedProducts = products.reduce((acc: GlobalVariant[], element) => {
    if (Object.keys(selected).includes(element.id.toString())) {
      return [element, ...acc];
    }
    return [...acc, element];
  }, []);
  return sortedProducts;
};

export const scrollToSection = (section: string) => {
  const sectionElement = document.getElementById(`section-${section}`);
  const scrollableElement = document.querySelector(
    '.bundle-scrollable-section',
  );
  if (sectionElement && scrollableElement) {
    // Top height of the element
    const elementHeightPosition = sectionElement.offsetTop;
    const stickyHeaderHeight = calculateStickyHeaderHeight();
    scrollableElement.scrollTo({
      top: elementHeightPosition - stickyHeaderHeight,
      behavior: 'smooth',
    });
  }
};

// Private function
const calculateStickyHeaderHeight = () => {
  const headerSection = document.querySelector('.header-section');
  const navHeader = document.querySelector('.bundle-section__nav');
  const stickyHeader = document.querySelector(
    '.bundleapp-modal__container--complete-screen--header-container',
  );
  const stepperHeader = document.querySelector('.bundle-stepper-header');
  const productOptionsHeader = document.querySelector(
    '.awtomatic-bab-options-container',
  );
  const progressBar = document.querySelector('.awtomatic-progress-container');

  const headerSectionHeight = headerSection?.clientHeight || 0;
  const navHeaderHeight = navHeader?.clientHeight || 0;
  const stickyHeaderHeight = stickyHeader?.clientHeight || 0;
  const stepperHeaderHeight = stepperHeader?.clientHeight || 0;
  const productOptionsHeaderHeight = productOptionsHeader?.clientHeight || 0;
  const progressBarHeight = progressBar
    ? window.getComputedStyle(progressBar).order === '1'
      ? progressBar.clientHeight
      : 0
    : 0;

  return (
    progressBarHeight +
    headerSectionHeight +
    productOptionsHeaderHeight +
    stepperHeaderHeight +
    stickyHeaderHeight +
    navHeaderHeight +
    40
  ); // Add some extra space
};

export const checkSectionsLimits = (
  sections: BabSection[],
  selectedProductsBySection: Record<string, Record<string, number>>,
  bundleSize: number,
): BabSection[] => {
  const sectionOutOfLimit = sections.filter((section) => {
    const { MinSize, MaxSize } =
      findSectionSettings(section.settings, bundleSize) || {};
    const min = toNumber(MinSize);
    const max = toNumber(MaxSize);
    const selectedProducts = selectedProductsBySection[section.id];
    const selectedProductsCount =
      selectedProducts &&
      Object.values(selectedProducts).reduce((acc, count) => acc + count, 0);

    if (!selectedProductsCount && min) return section;
    return (
      (min && selectedProductsCount < min) ||
      (max && selectedProductsCount > max)
    );
  });

  return sectionOutOfLimit;
};

export const findSectionSettings = (
  settings: BundleSetting[] = [],
  bundleSize: number,
): BundleSetting | undefined => {
  return (
    settings &&
    (settings?.find(({ Size }) => bundleSize === toNumber(Size)) || settings[0])
  );
};

export const calculateErrorClassAndLabel = (
  selectedProductsBySection: Record<string, number>,
  settings: BundleSetting,
) => {
  if (
    isEmpty(settings) ||
    isUndefined(settings) ||
    isEmpty(selectedProductsBySection) ||
    isUndefined(selectedProductsBySection)
  ) {
    return { errorClass: '', errorLabel: '' };
  }

  const { MinSize = 0, MaxSize = Infinity, ErrorLabel = '' } = settings;
  const min = toNumber(MinSize);
  const max = toNumber(MaxSize);
  const selectedProductsCount = Object.values(selectedProductsBySection).reduce(
    (acc, curr) => acc + curr,
    0,
  );

  if (selectedProductsCount >= max) {
    return {
      errorClass: `awt-is-max-size-reached ${
        selectedProductsCount > max ? 'awt-is-max-size-error' : ''
      }`,
      errorLabel: ErrorLabel,
    };
  }

  if (selectedProductsCount <= min) {
    return {
      errorClass: `awt-is-min-size-reached ${
        selectedProductsCount < min ? 'awt-is-min-size-error' : ''
      }`,
      errorLabel: ErrorLabel,
    };
  }

  return { errorClass: '', errorLabel: '' };
};

export const assignInitialSelectedProducts = (
  initialSelectedProducts: Record<string, number> = {},
  sections: BabSection[] = [],
): Record<string, Record<string, number>> => {
  return sections.reduce((acc, section) => {
    const { id } = section;
    const selectedProductsInThisSection = Object.entries(
      initialSelectedProducts,
    ).reduce((acc, [productId, count]) => {
      let variantSelected = null;
      if (!isEmpty(section.collections)) {
        variantSelected = section.productsInCollections
          .flat()
          .find(({ productData }) => {
            return productData.variants.find(({ id }) => id === +productId);
          });
      } else {
        variantSelected = section.products
          .flatMap(({ Variants }) => Variants)
          .find(({ VariantId }) => getIdFromGid(VariantId) === productId);
      }
      if (variantSelected) {
        return { ...acc, [productId]: count };
      }
      return acc;
    }, {});

    if (selectedProductsInThisSection) {
      return { ...acc, [id]: selectedProductsInThisSection };
    }
    return acc;
  }, {});
};

export const getKindOfLimit = (settings: BundleSetting) => {
  const { MinSize, MaxSize } = settings || {};

  if (MinSize && MaxSize) {
    return LimitTypesEnum.BOTH;
  }

  if (MinSize) {
    return LimitTypesEnum.MINIMUM;
  }

  if (MaxSize) {
    return LimitTypesEnum.MAXIMUM;
  }

  return '';
};
